import type { ReactNode } from 'react'
import {
  BanIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  CircleIcon,
  InfoIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from 'cn'

/**
 * A status, as a word and a shape — **never as a color alone**.
 *
 * Roughly one reader in twelve cannot separate the green from the amber, and in this product the
 * status column is the one that decides whether somebody publishes a page twice, chases an invoice
 * that is already paid, or reallocates a stand that is sold. The console already understood this in
 * one place: the CMS list renders a colored chip **and** a date line, with a comment saying why.
 * This is that decision, made available everywhere and made harder to get wrong.
 *
 * So every badge carries three redundant signals: the label (which is the real content), an icon
 * whose *shape* differs per tone, and the color. Remove any one and it still reads.
 *
 * **The tone is chosen by the caller, not derived here**, and that is deliberate. What
 * `payment_pending` means is knowledge that belongs to `features/billing`, not to a component
 * library — the same word means different things to an invoice and to a stand booking, and a map
 * living here would be this package quietly owning the product's vocabulary. Each feature maps its
 * own statuses to a tone; this renders the result.
 */

const badge = cva(
  'inline-flex w-fit shrink-0 items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        /** Nothing is happening and nothing is wrong: a draft, an archive, an unknown. */
        neutral: 'bg-muted text-muted-foreground border-transparent',
        /** Settled, paid, published, confirmed. */
        success: 'bg-success/12 text-success border-success/25',
        /** Somebody has to act, and it is not an error yet. */
        warning: 'bg-warning/12 text-warning border-warning/25',
        /** Refused, failed, canceled, overdue. */
        danger: 'bg-destructive/12 text-destructive border-destructive/25',
        /** Worth knowing, requires nothing. */
        info: 'bg-info/12 text-info border-info/25',
        /** Waiting on somebody else. Distinct from `warning`, which is waiting on *you*. */
        pending: 'bg-muted text-foreground border-border',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export type StatusTone = NonNullable<VariantProps<typeof badge>['tone']>

/**
 * One icon per tone, and **no two share a silhouette**.
 *
 * That is the requirement rather than a preference: if `success` and `info` were both a circle,
 * the redundancy would be decorative and the color would be carrying the meaning again.
 */
const ICON: Record<StatusTone, ReactNode> = {
  neutral: <CircleDashedIcon />,
  success: <CheckCircle2Icon />,
  warning: <TriangleAlertIcon />,
  danger: <BanIcon />,
  info: <InfoIcon />,
  pending: <CircleIcon />,
}

export function StatusBadge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: StatusTone
  /** The label. A word somebody can read — not a code, and never empty. */
  children: ReactNode
  className?: string
}) {
  return (
    <span className={cn(badge({ tone }), className)}>
      {/*
        `aria-hidden`, because the label beside it already says this. Without it a screen reader
        announces the status twice — once as an image role and once as the word — which is the
        usual way a well-meant icon makes a table slower to listen to.
      */}
      <span aria-hidden="true" className="contents">
        {ICON[tone]}
      </span>
      {children}
    </span>
  )
}
